// Korean catalogue.
//
// Mirrors en.js. Anything missing here falls back to the English string, so
// this can be filled in incrementally without shipping visible breakage.
//
// Terminology follows Korean index and asset-management convention rather than
// literal translation — 정기변경 for reconstitution, 편출 for the cull, 비중 이탈률 for
// relative drift, 차익 실현 for profit-taking. "Sleeve" has no settled Korean
// equivalent and is rendered 전략. These are the choices most worth a second
// opinion from a native reader; see the terminology note in the PR.

const ko = {
    // See the note in en.js. FMP's taxonomy is Yahoo-style rather than strict
    // GICS, so these follow the Korean names those categories are usually given
    // in domestic securities apps rather than a literal translation.
    data: {
        sector: {
            Crypto: '가상자산',
            // Crypto sectors, hard-coded per asset in the backend's sectors.ts.
            'Layer 1': '레이어 1',
            'Layer 2': '레이어 2',
            'Liquid Staking': '리퀴드 스테이킹',
            Stablecoin: '스테이블코인',
            DeFi: '디파이',
            AI: 'AI',
            'AI Agents': 'AI 에이전트',
            DePIN: '디핀(DePIN)',
            Gaming: '게임',
            'NFT & Art': 'NFT·아트',
            Social: '소셜',
            Infrastructure: '인프라',
            'Other crypto': '기타 가상자산',
            'Basic Materials': '기초소재',
            'Communication Services': '커뮤니케이션 서비스',
            'Consumer Cyclical': '경기소비재',
            'Consumer Defensive': '필수소비재',
            Energy: '에너지',
            'Financial Services': '금융',
            Healthcare: '헬스케어',
            Industrials: '산업재',
            'Real Estate': '부동산',
            Technology: '정보기술',
            Utilities: '유틸리티',
            // Not FMP's — the app buckets funds separately so they do not take
            // an operating company's sector. See fmpSectorProvider.
            'Funds & ETFs': '펀드·ETF',
            // Nor is this one. Cash is a bucket of its own rather than a
            // failure to classify — see sectorProvider.fallbackSector.
            Cash: '현금',
        },
        assetType: {
            Crypto: '가상자산',
            'Common Stock': '보통주',
            'American Depositary Receipt': '주식예탁증서(ADR)',
            'Open Ended Fund': '개방형 펀드',
            'Security type is not defined': '미분류',
            // IBKR reports subCategory codes rather than names.
            COMMON: '보통주',
            ADR: '주식예탁증서(ADR)',
            STK: '주식',
            OPT: '옵션',
            CASH: '현금',
        },
    },

    lang: { label: '언어', viewsAria: '포트폴리오 보기' },

    nav: {
        totalPortfolio: '전체 포트폴리오',
        artCollection: '아트 컬렉션',
        transactions: '거래 내역',
        notes: '메모',
        kanoseiIndex: 'Kanosei 인덱스',
        cryptoHoldings: '가상자산',
        privateInvestments: '비상장 투자',
        stockInvestments: '주식 투자',
        admin: '관리',
        settings: '설정',
        portfolio: '포트폴리오',
        tagline: '사모 투자 펀드',
    },

    common: {
        settings: '설정',
        signOut: '로그아웃',
        signIn: '로그인',
        portfolio: '포트폴리오',
        backToPortfolio: '포트폴리오로 돌아가기',
        all: '전체',
        table: '표',
        heatmap: '히트맵',
        filter: '검색…',
        save: '저장',
        cancel: '취소',
        edit: '수정',
        delete: '삭제',
        asOf: '{when} 기준',
        loadingError: '이 화면을 불러오지 못했습니다.',
    },

    holdings: {
        title: '포트폴리오',
        error: '포트폴리오를 불러오지 못했습니다.',
        emptyBefore: '보유 종목이 없습니다.',
        emptyLink: '설정',
        emptyAfter: '에서 증권 계좌를 연결하세요.',
        refresh: '증권사에서 새로고침',
        refreshing: '새로고침 중입니다. 잠시 걸립니다…',
        refreshFailed: '지금은 새로고침하지 못했습니다 — {reason} 아래 수치는 마지막으로 정상 동기화된 내용입니다.',
        notices_one: '알림 {count}건 — 메모 탭에서 확인하고 숨길 수 있습니다',
        notices_other: '알림 {count}건 — 메모 탭에서 확인하고 숨길 수 있습니다',
    },

    private: {
        title: '비상장 투자',
        scope: '직접 관리하는 벤처 및 DAO 투자입니다. 진행 중인 투자만 전체 포트폴리오에 포함되며, 회수된 투자의 회수금은 이미 다른 계좌에 반영되어 있습니다.',
        active: '진행 중인 투자',
        exits: '회수 완료',
        count: '투자 {count}건',
        entries: '{count}건',
        add: '+ 투자 추가',
        removeRound: '삭제',
        delete: '투자 삭제',
        confirmDelete: '{name} 투자와 모든 내역을 삭제할까요? 되돌릴 수 없습니다.',
        edit: '편집',
        editAria: '{name} 편집',
        toExits: '회수로 이동',
        toExitsAria: '{name}을(를) 회수 완료로 이동',
        toActive: '진행 중으로 이동',
        toActiveAria: '{name}을(를) 진행 중인 투자로 이동',
        na: '해당 없음',
        yes: '예',
        no: '아니요',
        usdPlaceholder: 'USD',
        quantityPlaceholder: '수량',
        assetPlaceholder: '자산',
        quantityAria: '{field} 수량',
        markQuantityAria: '미실현 평가액 수량',
        markAssetAria: '미실현 평가액 자산',
        sectorPlaceholder: '섹터',
        subSectorPlaceholder: '세부 분류',
        sectorAria: '섹터',
        subSectorAria: '세부 분류',
        emptyActive: '진행 중인 투자가 없습니다.',
        emptyExits: '회수된 투자가 없습니다.',
        live: '실시간',
        holdings: '보유 종목 {count}개',
        kinds: { VC: 'VC', DAO: 'DAO', Fund: '펀드', 'Real Estate': '부동산' },
        col: {
            name: '투자처',
            kind: '유형',
            sector: '섹터',
            invested: '최초 취득원가',
            distributed: '실현 손익 / 분배금',
            mark: '미실현 평가액',
            pnl: '총 손익',
            realised: '실현 손익',
            comments: '메모',
            actions: '작업',
        },
        round: {
            description: '내용',
            date: '일자',
            amount: '금액 (USD)',
            asset: '실제 투입 자산',
            valuation: '밸류에이션',
            units: '수량',
            aria: '{field} {n}',
            add: '+ 취득원가 내역 추가',
            removeAria: '{n}번째 취득원가 내역 삭제',
        },
        dist: {
            description: '내용',
            date: '일자',
            amount: '금액 (USD)',
            asset: '실제 수령 자산',
            sold: '매도 여부',
            soldInto: '매도 후 자산',
            aria: '{n}번째 분배금 {field}',
            add: '+ 분배금 추가',
            removeAria: '{n}번째 분배금 삭제',
        },
        summary: {
            mark: '미실현 평가액',
            returned: '회수액',
            invested: '투자금',
            counts: '투자 {count}건 · 라운드 {rounds}회',
            pnl: '총 손익',
            withDistributed: '분배금 {amount} 포함',
            realised: '실현 손익',
            multiple: '배수',
            unmarked: '평가액이 없는 투자 {count}건',
            unreturned: '분배금 기록이 없는 회수 투자 {count}건',
        },
        errors: {
            load: '비상장 투자 내역을 불러오지 못했습니다.',
            save: '투자를 저장하지 못했습니다.',
            move: '투자를 이동하지 못했습니다.',
            delete: '투자를 삭제하지 못했습니다.',
            invalid: '투자 정보를 확인하세요.',
            session: '세션이 만료되었습니다. 다시 로그인하세요.',
            offline: '서버에 연결할 수 없습니다.',
            name: '투자처 이름을 입력하세요.',
            currentMark: '미실현 평가액과 수량은 0 이상의 숫자여야 합니다.',
            rounds: '취득원가의 금액, 밸류에이션, 수량은 0 이상의 숫자여야 합니다.',
            distributions: '분배금 금액은 0 이상의 숫자여야 합니다.',
        },
    },

    accounts: {
        aria: '계좌',
        ibkrIndex: 'IBKR (Kanosei 인덱스)',
        ibkr: 'IBKR',
        ibkrOther: 'IBKR (기타)',
        fidelity: 'Fidelity',
        crypto: '가상자산',
        private: '비상장',
        tagIndex: '인덱스',
        tagOther: '기타',
        tagWip: '작업 중',
        cryptoWip: '손익 내역 작업 중: Arbitrum과 Base 내역이 아직 완전하지 않아 순손익과 순입금은 아직 표시하지 않습니다. 평가액은 실시간이며 정확합니다.',
        cryptoHistory: '일별 내역은 {date}부터 기록됩니다.',
        cryptoScopeWip: '가상자산 지갑은 추적을 시작한 {date}부터 평가액에 포함되며, 손익 내역은 작업 중입니다.',
        noHistory: '이 증권사는 일별 평가액과 기간별 손익 내역을 제공하지 않습니다.',
        noSplit: '이 증권사는 취득원가를 제공하지 않아 시가평가와 실현손익을 분리할 수 없습니다. 두 항목의 합계는 순손익에 이미 반영되어 있습니다.',
        noHistoryScope: '일별 내역 없음 — 이 증권사는 제공하지 않습니다.',
        partialHistoryScope: '{names} 제외 — 해당 계좌의 일별 내역은 제공되지 않습니다.',
        privateLabel: '비상장 투자',
        cryptoScope: '가상자산 지갑은 추적을 시작한 {date}부터 평가액에 포함됩니다. 손익 내역은 최초 기록부터 집계되므로 여기가 아닌 가상자산 카드와 보기에 표시됩니다.',
        cryptoNote: '온체인 기준 평가. 손익 내역은 최초 기록부터 집계되어 전체 기간에 표시되며, 일별 내역은 {date}부터 기록됩니다.',
        cryptoInception: '최초 온체인 기록부터 집계. 대가 없이 들어온 토큰(스테이킹 보상, 에어드롭 포함)은 도착 시점의 가치로 입금에 포함되며, 가스비는 평가손익에 포함됩니다. 일별 내역은 {date}부터 기록됩니다.',
        privateScope: '비상장 투자는 {date}부터 입력된 평가액으로 포함됩니다. 손익 내역은 최초 투자부터 집계되므로 여기가 아닌 비상장 카드와 보기에 표시됩니다.',
        privateNote: '비상장 투자 탭의 평가액 기준. 손익 내역은 최초 투자부터 집계되어 전체 기간에 표시되며, 일별 내역은 {date}부터 기록됩니다.',
        privateInception: '최초 투자부터 집계. 회수된 투자는 회수 평가액으로 출금 처리되며, 평가액이 없는 투자는 손익에서 제외됩니다. 일별 내역은 {date}부터 기록됩니다.',
    },

    timeframe: {
        aria: '기간',
        '1d': '1일',
        '1w': '1주',
        // 최근 한 달이 아니라 당월 누계. 증권사가 내역을 분해해 제공하는 기간이
        // 당월 누계이며, 아래 수치가 실제로 포괄하는 기간이기도 하다.
        mtd: '당월',
        '3m': '3개월',
        '6m': '6개월',
        ytd: '연초 이후',
        '1y': '1년',
        all: '전체',
    },

    positions: {
        symbol: '종목코드',
        name: '종목명',
        account: '계좌',
        sector: '섹터',
        qty: '수량',
        price: '단가',
        value: '평가금액',
        unrealised: '평가손익',
        unrealisedPct: '평가손익률',
        pctNav: '비중',
        opened: '매수일',
        cashTag: '(현금)',
        count_one: '{count}종목',
        count_other: '{count}종목',
        filterAria: '보유 종목 검색',
        viewAria: '보유 종목 보기 방식',
        heatmapLegend: '면적: 보유 규모 · 색상: 매입가 대비 평가손익',
        heatmapEmpty: '조건에 맞는 종목이 없습니다.',
    },
    crypto: {
        title: '가상자산',
        scopeAria: '지갑',
        count: '{count}종목',
        members: '{count}개 포지션',
        chainCount: '{count}개 체인',
        walletCount: '{count}개 지갑',
        expand: '{symbol} 상세 보기',
        collapse: '{symbol} 상세 닫기',
        balancesAria: '잔고 규모',
        balancesMain: '보유 자산',
        balancesSmall: '$100 미만 잔고, 합계: ${total}',
        pnlLine: '평가손익 {pnl} USD ({pct})',
        pnlPartial: '매입가가 아직 없는 {count}개 종목은 제외되었습니다.',
        indexing: '온체인 거래 내역을 불러오는 중… 완료되면 평균 매입가와 매수일이 표시됩니다.',
        empty: '추적 중인 지갑에 $1 이상 보유한 자산이 없습니다.',
        hidden: '{count}개 토큰 미표시: 시세가 없거나 $1 미만입니다.',
        method: '평균단가 기준입니다. 스왑은 실제 지불한 금액으로, 대가 없이 들어온 토큰은 입금 시점의 시세로 평가합니다. 추적 중인 지갑 간 이체는 기존 매입가와 날짜를 유지합니다. Aave 예치·대출은 해당 자산으로 표시하며(대출은 음수 수량), 리퀴드 스테이킹 토큰은 발행처를 함께 표시합니다. 하이퍼리퀴드는 현물 기준으로 표시합니다: 무기한 롱은 진입가와 명목 가치로 보유 자산처럼, 숏은 USDC에 포함하며, USDC는 계정 자산에서 롱 포지션 가치를 뺀 금액입니다. 유동성 풀 포지션은 제외됩니다.',
        col: {
            token: '토큰',
            chain: '체인',
            wallet: '지갑',
            qty: '수량',
            avgEntry: '평균 매입가',
            price: '현재가',
            value: '평가금액',
            pnl: '평가손익',
            pnlPct: '수익률',
            firstPurchase: '최초 매수일',
            lastPurchase: '최근 매수일',
            held: '보유 기간',
        },
        held: {
            days: '{n}일',
            months: '{m}개월',
            yearsMonths: '{y}년 {m}개월',
        },
        chains: {
            ethereum: '이더리움',
            arbitrum: '아비트럼',
            base: '베이스',
            solana: '솔라나',
            hyperliquid: '하이퍼리퀴드',
        },
        kind: {
            staked: '{protocol} · 스테이킹',
            supply: '{protocol} · 예치',
            borrow: '{protocol} · 대출',
            cash: '{protocol} · 현금',
        },
        openOn: '{site}에서 {symbol} 열기',
        wallets: {
            heading: '지갑',
            empty: '등록된 지갑이 없습니다. 아래에 주소를 추가하세요.',
            chainAria: '체인',
            addressAria: '지갑 주소',
            addressPlaceholder: '주소…',
            labelAria: '지갑 이름',
            labelPlaceholder: '이름 (선택)',
            add: '지갑 추가',
            remove: '삭제',
            removeAria: '{name} 지갑 삭제',
            confirmRemove: '{name} 지갑 추적을 중단할까요? 불러온 거래 내역도 함께 삭제됩니다.',
            synced: '{when} 동기화',
            indexing: '거래 내역 불러오는 중…',
            notConfigured: '설정 필요: {provider} API 키가 없습니다',
            syncError: '마지막 동기화 실패, 다시 시도합니다',
            balanceError: '지금은 잔고를 읽을 수 없습니다',
            rename: '이름 변경',
            nameAria: '지갑 이름',
        },
        errors: {
            load: '가상자산 내역을 불러오지 못했습니다.',
            save: '지갑을 추가하지 못했습니다.',
            rename: '지갑 이름을 변경하지 못했습니다.',
            remove: '지갑을 삭제하지 못했습니다.',
            sync: '온체인 거래 내역을 불러오지 못했습니다. 새로고침하여 다시 시도하세요.',
            badAddress: '선택한 체인의 주소가 아닙니다.',
            duplicate: '이미 추적 중인 지갑입니다.',
            invalid: '체인과 주소를 확인하세요.',
            session: '세션이 만료되었습니다. 다시 로그인해 주세요.',
            offline: '서버에 연결할 수 없습니다.',
        },
    },
    themes: {
        column: '테마',
        notAssigned: '미지정',
        manage: '테마',
        empty: '테마가 없습니다. 테마를 추가한 뒤 테마 열에서 선택하세요.',
        newPlaceholder: '새 테마…',
        newAria: '새 테마 이름',
        add: '추가',
        deleteAria: '{name} 테마 삭제',
        assignAria: '{symbol} 테마',
        confirmDelete: '"{name}" 테마를 삭제할까요?',
        confirmDeleteAssigned: '"{name}" 테마를 삭제할까요? {count}개 종목이 미지정으로 바뀝니다.',
        errors: {
            load: '테마를 불러오지 못했습니다.',
            save: '테마를 저장하지 못했습니다.',
            delete: '테마를 삭제하지 못했습니다.',
            duplicate: '같은 이름의 테마가 이미 있습니다.',
            gone: '삭제된 테마입니다. 페이지를 새로고침하면 최신 목록이 표시됩니다.',
            session: '세션이 만료되었습니다. 다시 로그인해 주세요.',
            offline: '서버에 연결할 수 없습니다.',
        },
    },

    activity: {
        title: '거래 내역',
        error: '거래 내역을 불러오지 못했습니다.',
        emptyBefore: '거래 내역이 없습니다.',
        emptyLink: '설정',
        emptyAfter: '에서 Fidelity를 연결하세요.',
        scope: 'Fidelity 계좌만 표시됩니다 — Interactive Brokers의 Flex 조회는 보유 종목과 순자산만 제공하며 체결 내역은 포함하지 않습니다.',
        date: '일자',
        type: '구분',
        symbol: '종목코드',
        description: '내용',
        qty: '수량',
        price: '단가',
        amount: '금액',
        fee: '수수료',
        count_one: '{count}건',
        count_other: '{count}건',
        net: '순액 {amount} {currency}',
        filterAria: '거래 내역 검색',
        showMore: '{count}건 더 보기',
        showing: '전체 {total}건 중 {shown}건 표시',
        group: {
            trades: '매매',
            income: '수익',
            transfers: '입출금',
            fees: '수수료·세금',
            options: '옵션',
        },
    },

    notices: {
        cryptoUnconfiguredMany: '가상자산: 지갑 {count}개가 포함되지 않았습니다 — 서버에 {chains} 설정이 없습니다.',
        cryptoUnconfiguredOne: '가상자산: {name} 지갑이 포함되지 않았습니다 — 서버에 {chain} 설정이 없습니다.',
        cryptoSomeUnreadable: '일부 가상자산 지갑을 읽지 못해 마지막으로 확인된 잔고를 표시합니다.',
        cryptoUnreadable: '가상자산 지갑을 읽지 못해 마지막으로 확인된 잔고를 표시합니다.',
        cryptoCurrency: '가상자산 지갑은 포함되지 않았습니다: 지갑은 USD로 평가되지만 포트폴리오 기준 통화는 {ccy}입니다.',
    },

    notes: {
        title: '메모',
        error: '메모를 불러오지 못했습니다.',
        noticesHeading: '알림',
        noticesEmpty: '확인이 필요한 항목이 없습니다.',
        dismiss: '숨기기',
        restore: '복원',
        showDismissed: '숨긴 알림 {count}건 보기',
        hideDismissed: '숨긴 알림 {count}건 접기',
        heading: '메모',
        lede: '로그인한 모든 사용자와 공유됩니다. 종목 정보만으로는 알 수 없는 판단의 근거를 남겨두는 곳입니다 — 왜 비중을 늘렸는지, 무엇을 기다리고 있었는지.',
        placeholder: '메모를 입력하세요…',
        add: '메모 추가',
        empty: '아직 메모가 없습니다.',
        edited: '수정됨',
        newAria: '새 메모',
        editAria: '메모 수정',
    },

    index: {
        title: 'Kanosei 인덱스',
        tabs: {
            portfolio: '보유 종목',
            rebalance: '리밸런싱',
            targets: '목표 비중',
            rules: '규칙',
        },
        error: '인덱스를 불러오지 못했습니다.',
        scope: '{account} 계좌를 기준으로 산출합니다.',
        sleeveOver: '자산 슬리브 {names}개 종목 (추가 납입 시 {count}개 편입) — 다음 정기변경까지 목표 비중 합계 {target}%.',
        deployment: '{pct}% 투자 · 미투자 현금 {cash} {currency}.',
        ranking: '순위 {date}',
        tabsAria: '인덱스 보기',
        view: {
            aria: '인덱스 보기 방식',
            all: '전체',
            assetSleeve: '자산 전략',
            growthEtfs: '성장 ETF',
            empty: '이 전략에 보유 종목이 없습니다.',
            legend: '면적: 보유 규모 · 색상: 목표 비중 대비 이탈',
            liveRank: '현재 순위',
            pctInvested: '투자원금 비중',
            target: '목표',
        },
    },

    countdown: {
        bandCheck: '허용범위 점검',
        reconstitution: '정기변경',
        today: '오늘',
        daysLeft_one: '{count}일 남음',
        daysLeft_other: '{count}일 남음',
        withinBand: '모든 종목이 허용범위 이내',
        outsideBand_one: '{count}종목 허용범위 이탈',
        outsideBand_other: '{count}종목 허용범위 이탈',
        replaced_one: '구성종목 {count}개 교체 예정',
        replaced_other: '구성종목 {count}개 교체 예정',
        noChanges: '예정된 구성종목 변경 없음',
    },

    performance: {
        netPl: '순손익',
        netDeposits: '순입금',
        notEarned: '입금액이며 수익이 아님',
        markToMarket: '시가평가',
        realised: '실현손익',
        dividends: '배당·이자',
        fees: '수수료',
        accruals: '미수수익·환율',
        changeInValue: '평가액 변동',
        changePct: '평가액 {pct}% 변동',
        twr: '시간가중수익률 {pct}%',
        onCapital: '투입 자본 대비 {pct}',
        period: '{from} ~ {to}',
        noBreakdown: '평가액 변동만 표시됩니다 — 증권사는 당월 누계, 연초 이후, 전체 기간에 대해서만 손익 내역을 제공합니다.',
        noBreakdownInception: '평가액 변동만 표시 — 손익 내역은 최초 기록부터 집계됩니다. 전체 기간을 선택하면 볼 수 있습니다.',
    },

    navChart: {
        title: '포트폴리오 평가액',
        change: '{amount} {currency} 변동',
        changeWithDeposits: '입금액 포함 {amount} {currency} 변동',
    },

    breakdown: {
        title: '구성 분석',
        aria: '구성 분석 보기 방식',
        chartAria: '자산 구성 분석',
        assetType: '자산 유형',
        sector: '섹터',
        country: '국가',
        currency: '통화',
        bars: '막대',
        pie: '원형',
        heatmap: '히트맵',
        unclassified: '미분류',
        legend: '면적: 포트폴리오 내 비중 · 색상: 매입가 대비 평가손익',
        pnlTitle: '매입가 대비 평가손익',
        noData: '아직 {dimension} 데이터가 없습니다 — 증권사가 제공하지 않으므로 시세 데이터 공급자가 필요합니다.',
    },

    rebalance: {
        asset: '종목',
        current: '현재',
        target: '목표',
        drift: '비중 이탈률',
        band: '허용범위',
        trade: '매매금액',
        nextAction: '조치',
        ok: '모든 종목이 허용범위 이내입니다. 다음 월간 점검에서 조치할 사항이 없습니다.',
        breaching: '{count}개 종목 허용범위 이탈 — {symbols}',
        leaving: '{symbols}이(가) 현재 상위 20위 밖에 있어 다음 정기변경에서 교체됩니다. 그때까지 비중은 고정됩니다.',
        bandTitle: '허용범위 투자원금의 {low}%–{high}%',
        liveRank: '현재 {rank}위',
        entered: '{rank}위로 편입',
        shares: '{count}주',
        footnote: '허용범위는 목표 대비 상대값입니다. 자산 전략은 ±{asset}%이고, 성장 ETF는 상단만 있는 +{growth}%입니다 — 목표에 못 미치는 섹터는 다른 종목을 축소해 채우지 않고 새 자금을 기다립니다. {cap}%를 넘는 종목은 예외 없이 {cap}%까지 축소합니다. 비중은 투자원금 대비이며 현금은 제외하므로, 투자되지 않은 잔고가 모든 비중을 한꺼번에 움직이지 않습니다. 허용범위 이탈은 목표 비중까지, 상한 초과는 상한까지 되돌리며, 허용범위 경계에서 멈추는 경우는 없고 예정된 추가 납입도 고려하지 않습니다. 금액 단위는 {currency}입니다.',
    },

    targets: {
        symbol: '종목코드',
        asset: '종목',
        marketCap: '시가총액',
        weight: '비중',
        status: '상태',
        theme: '테마',
        current: '현재',
        growthSleeve: '성장 전략 — {pct}%',
        held: '보유',
        notHeld: '미보유',
        newConstituent: '신규 편입',
        unavailable: '시가총액 순위를 불러올 수 없습니다.',
        unavailableWhy: '시가총액 순위를 불러올 수 없습니다 — {reason}.',
        lede: '{asOf} 기준 순위로 산출한 {date} 정기변경 예상안입니다.',
        entering: '편입: {symbols}.',
        leaving: '편출: {symbols}.',
        assetSleeve: '자산 전략',
        growthSleeveRow: '성장 전략',
        growthLede: '시가총액으로 순위를 매기지 않습니다. 5개 테마를 각 2%씩 두며, 과거 수익률이 아니라 편입 종목의 폭, 유동성, 테마 순도를 기준으로 검토합니다.',
    },

    action: {
        hold: '허용범위 이내',
        trim_band: '목표 대비 {rel}% 초과, ±{band}% 허용범위 이탈 — {target}%로 축소',
        add_band: '목표 대비 {rel}% 미달, ±{band}% 허용범위 이탈 — {target}%로 추가 매수',
        trim_cap: '{cap}% 상한 초과 — {cap}%로 축소',
        buy_missing: '미보유 — {target}%까지 매수',
        sell_not_in_index: '보유 중이나 인덱스 구성종목이 아님 — 매도',
        hold_leaving: '허용범위 이내이나 현재 상위 20위 밖 — 다음 정기변경에서 교체',
        label: {
            hold: '유지',
            trim_band: '축소',
            add_band: '추가',
            trim_cap: '축소(상한)',
            buy_missing: '매수',
            sell_not_in_index: '매도',
        },
    },

    settings: {
        title: '연결된 계좌',
        empty: '아직 연결된 계좌가 없습니다.',
        managed: '관리형',
        managedTitle: 'AWS Secrets Manager에서 설정됨',
        unlink: '연결 해제',
        rename: '이름 변경',
        nicknameAria: '계좌 별칭',
        query: '조회 ID {ref}',
        linkIbkr: 'Interactive Brokers 연결',
        linkFidelity: 'Fidelity 연결',
        label: '이름',
        queryId: '조회 ID',
        token: '토큰',
        linkAccount: '계좌 연결',
        loading: '불러오는 중…',
        linking: '연결 중…',
        opening: '여는 중…',
        connectFidelity: 'Fidelity 연결',
        linkedNotice: '계좌가 연결되었습니다. 다음 새로고침 이후 보유 종목이 표시됩니다.',
        indexAccount: 'Kanosei 인덱스 계좌',
        indexAccountHelp: 'IBKR Flex 쿼리 하나가 여러 계좌를 포함할 수 있으며, 그중 하나만 인덱스입니다. 인덱스 페이지의 비중·괴리·리밸런싱·재구성은 모두 여기서 선택한 계좌를 기준으로 산출하며, 다른 계좌의 보유 종목은 제외합니다.',
        indexAccountLabel: '인덱스 계좌',
        indexAccountAuto: '자동 선택',
        indexAccountDerived: '현재 {account}를 사용 중입니다. 보유 종목이 인덱스와 일치하여 자동 선택되었습니다. 고정하려면 직접 선택하세요.',
        indexSavedNotice: '인덱스 계좌를 저장했습니다.',
        fidelityLinkedNotice: 'Fidelity가 연결되었습니다. 증권사의 최초 동기화가 끝나면 보유 종목이 표시되며, 몇 분 정도 걸릴 수 있습니다.',
        ibkrBefore: 'IBKR에서 ',
        ibkrPath: 'Performance & Reports \u2192 Flex Queries',
        ibkrAfter: '로 이동합니다. Open Positions(로트 상세 포함), Trades, Cash Report를 포함한 Activity Flex Query를 만든 뒤, Flex Web Service Configuration에서 토큰을 발급하세요. 이 토큰은 읽기 전용이라 매매하거나 자금을 이체할 수 없습니다.',
        fidelityBefore: 'Fidelity는 공개 API가 없어 ',
        fidelityBrand: 'SnapTrade',
        fidelityAfter: '의 호스팅 포털을 통해 연결됩니다. Fidelity 로그인은 SnapTrade 페이지에서 이루어지며, Fidelity 비밀번호는 이곳에 입력되지도, 저장되지도 않습니다. 이 연결은 읽기 전용이며 주문을 낼 수 없습니다.',
    },

    home: {
        heroSub: 'Kanosei는 공개 시장, 비상장 기업, 디지털 아트에 걸쳐 소수 종목에 집중한 포트폴리오를 운용합니다.',
        viewCollection: '컬렉션 보기',
        thesisHeading: '위임받은 범위를 채우기 위해서가 아니라, *관점*이 있는 곳에 배분합니다',
        thesisP1: 'Kanosei는 사모 투자 펀드입니다. 포트폴리오는 소수 종목에 집중되어 있으며 주식, 암호자산, 비상장 기업, 디지털 아트에 걸쳐 있습니다 — 모두 세계와 자본이 향하는 방향에 대한 저희의 관점을 담기 위한 것입니다.',
        thesisP2: '고정된 운용 범위를 두지 않습니다. 세상은 극도로 빠르게 변하고 있어 경직된 전제를 더는 고수할 수 없다고 보기 때문입니다.',
        collectionLabel: '컬렉션',
        collectionHeading: 'NFT의 과거와 현재, 그리고 앞으로의 모습을 담은 기록',
    },

    collection: {
        alt: '{artist}의 {title}',
        artistsCounted: '컬렉션에 포함된 아티스트 수',
        worksCounted: '수집한 작품 수',
        heading: '저희는 디지털 아트를 수집하고 투자하며, 블록체인을 신뢰가 필요 없는 창작의 토대로 활용합니다.',
        p1: '출처 증명은 한때 기관에 의존했습니다 — 갤러리의 말, 서랍 속 증서, 누군가가 보증해야 하는 소유 이력이었습니다. 온체인에서는 그것이 그저 사실입니다. 누가 언제 작품을 만들었는지, 그 이후 누가 소유해 왔는지를 누구나 검증할 수 있고 누구의 소유도 아닙니다. 저희가 수집을 통해 지나고 있는 변화가 바로 이것입니다. 아티스트와 그 작품의 가치를 알아보는 사람 사이에서 문지기를 걷어내고, 기록 자체를 매체의 일부로 만듭니다.',
        p2: '이 컬렉션은 폭넓은 작품을 아우릅니다. Trevor Jones와 Reuben Wu처럼 전통적 매체를 디지털로 옮겨오는 작업부터, ArtBlocks를 통한 Casey Reas와 Zeblocks의 제너러티브 작업처럼 디지털 아트의 가능성을 탐구하는 작업까지 포함합니다. Marvel, 애니메이션, 밈, 게임, 아름다운 풍경처럼 사회적 현상과 공유된 문화를 담아내는 작품 또한 컬렉션의 큰 축입니다.',
        p3: 'NFT 영역이 확장되며 기존의 맥락을 바꾸어 가는 가운데, 저희는 예술이 만들어지고 변형되는 방식에서 새로운 패러다임을 탐구하는 작품, 메타버스에 적용되는 작품을 발굴하고, 신진 아티스트와 소수 커뮤니티 출신 아티스트의 작업을 지원하고자 합니다.',
        p4: '개별 작품은 각 아티스트 고유의 스타일과 여정을 보여줍니다.',
        p5: '전체로서 저희 컬렉션은 NFT의 과거와 현재, 그리고 앞으로의 모습을 담은 기록이자 자료입니다.',
        curatedBy: '큐레이션:',
        and: '·',
        goToArtwork: '작품 보러 가기',
    },

    work: {
        by: '작가:',
        viewArtwork: '작품 보기',
    },

    footer: {
        rights: '\u00a9 {year} Kanosei All Rights Reserved',
    },

    login: {
        title: '포트폴리오',
        signIn: 'Google 계정으로 로그인',
        blurb: '이 영역은 Kanosei 계정 전용입니다. @kanosei.xyz Google 계정으로 로그인하세요.',
        domain: '{domain} 계정이 필요합니다.',
    },

    rules: {
        error: '인덱스 정의를 불러오지 못했습니다.',
        intro: 'Kanosei 인덱스의 운용 방식입니다. 구성종목과 비중은 인덱스 정의와 현재 순위에서 실시간으로 읽어오며, 기준값은 확정된 규칙입니다.',
        pendingLabel: '아직 엔진에 반영되지 않음',
        pending: '이탈폭 절반 조정, 섹터 상한, 차익 실현입니다. 허용범위를 이탈하면 절반이 아니라 목표까지 전부 되돌립니다. 그 밖의 내용은 모두 적용되어 있습니다 — ±{band}% 허용범위, 섹터의 상단 전용 허용범위, 상한 초과 시 상한까지만 축소, 그리고 분모에서 현금 제외입니다. 어느 쪽이든 이 페이지는 확정된 규칙을 설명합니다.',

        cadence: '주기',
        cadenceLede: '두 가지 주기가 있으며 모두 {inception} 설정일을 기준으로 합니다. 월간 점검 6회, 그리고 여섯 번째 점검을 겸하는 정기변경입니다.',
        bandCheck: '허용범위 점검',
        reconCull: '정기변경 + 편출',

        sleeves: '독립된 두 전략 구분',
        sleevesLede: '측정도 자금도 분리되어 있습니다 — 차익 실현만이 유일한 예외입니다. 자금 추가만이 비율을 {asset} / {growth}로 되돌립니다.',
        assetSleeve: '상위 20종목',
        assetSleeveNote: '20종목을 넘을 수 있습니다. 자금을 넣을 때마다 그날 기준 상위 20종목을 매수하므로, 새로 순위에 든 종목은 기존 보유 종목을 밀어내지 않고 추가됩니다. 정기변경마다 편출을 통해 다시 정확히 20종목으로 돌아옵니다.',
        growthSleeve: '신흥 섹터',
        growthSleeveNote: '최대 {max}개, 각각 동일 비중입니다.',

        schedule: '비중 체계',
        scheduleLede: '비중은 종목이 아니라 순위에 붙습니다 — 정기변경은 각 순위에 어떤 종목이 오는지를 바꿀 뿐, 비중 체계 자체는 바뀌지 않습니다.',
        rankingAsOf: '{date} 기준 순위입니다.',
        unheldNote: '흐리게 표시 — 현재 상위 20위 안에 있으나 보유하고 있지 않은 종목입니다.',

        bands: '허용범위 (실제 비율)',
        bandsLede: '각 종목은 목표 대비 ±{band}%까지 벗어나도 조치하지 않습니다. 허용범위를 이탈하면 *목표까지의 이탈폭 중 절반만* 좁힙니다 — 전부도 아니고, 허용범위 경계까지도 아닙니다. 1순위 {top} 기준으로 표시했습니다.',
        bandsWhy: '전부가 아니라 절반인 이유는, 전량 조정이 상승 흐름을 타고 있는 종목의 초과분을 모두 걷어내기 때문입니다. 허용범위 경계가 아니라 절반인 이유는, 경계는 고정된 선이고 상승하는 종목은 그 선에서 계속 멀어지기 때문입니다 — 목표의 두 배까지 오른 종목은 경계까지 되돌리면 35%를 매도하게 되지만, 이탈폭의 절반만 좁히면 25%입니다. 따라서 대부분의 종목은 목표에서 벗어난 상태로 유지되며, 이는 의도된 결과입니다.',
        addBack: '추가 매수',
        target: '목표',
        trim: '축소',
        hardCap: '단일 종목 상한',
        hardCapNote: '허용범위보다 먼저 확인하며 허용범위에 우선합니다. 목표가 아니라 상한까지만 축소합니다. 허용범위 조정이 느려진 지금, 단일 종목의 비중을 제한하는 유일한 장치입니다 — 월 20% 초과 성과가 지속되면 1순위 종목은 정확히 이 지점에서 안정됩니다.',
        cashNote: '현금은 모든 허용범위 점검에서, 그리고 비중을 계산하는 분모에서도 제외됩니다. 보유 종목 표에는 표시되지만 구성종목은 아닙니다.',
        sectorBandNote: '섹터 ETF에는 상단 허용범위만 적용됩니다. 상단을 넘으면 다른 종목과 마찬가지로 축소하지만, 목표에 못 미치더라도 그대로 둡니다 — 다른 종목을 매도해 섹터를 추가 매수하는 일은 없기 때문입니다.',

        adding: '자금 추가',
        addingLede: '두 전략 구분을 동일하게 다루는 유일한 이벤트이며, 비율을 {asset} / {growth}로 되돌리는 유일한 이벤트입니다.',
        whatBought: '매수 대상',
        whatBoughtV: '그날 기준 적격 상위 20종목을 위 비중대로, 그리고 운용 중인 각 섹터에 동일 비중으로 배분합니다',
        alreadyHeld: '이미 보유 중이면?',
        alreadyHeldV: '그날의 순위에 해당하는 비중만큼 추가 매수합니다. 이미 보유하고 있다는 것은 제외 사유가 아닙니다',
        existing: '기존 보유 종목',
        existingV: '*건드리지 않습니다.* 아무것도 매도하지 않고 수익 종목을 줄이지도 않습니다 — 자금 추가는 새 자금을 배분할 뿐 기존 포트폴리오를 리밸런싱하지 않습니다',

        sectors: '신흥 섹터',
        sectorsLede: '섹터는 개별 종목이 아니라 테마입니다 — 로보틱스는 ETF 2종, 우주도 2종입니다. 모든 상한과 기준값은 테마 합계를 기준으로 합니다.',
        sectorRules: '규칙',
        perSector: '섹터별',
        perSectorNote: '상한까지 축소합니다. 매도 대금은 다른 섹터로 배분됩니다.',
        allFive: '5개 섹터 합계',
        allFiveNote: '비례 축소합니다. 매도 대금은 포트폴리오 전체로 배분됩니다 — 이 경우는 전략 구분 내부에서 흡수할 수 없습니다.',
        profitTake: '차익 실현 기준',
        profitTakeNote: '목표의 두 배에 도달하면, 그 상태가 유지되는 매월 테마 평가액의 {pct}를 매도합니다.',
        sectorFundingNote: '섹터는 새로 납입한 자금으로만 추가 매수합니다. 섹터를 채우기 위해 다른 종목을 매도하는 일은 없으므로, 목표에 못 미치는 섹터는 다음 자금 추가 때까지 그대로 유지되며 그때 다른 섹터와 똑같이 통상적인 몫을 배정받습니다.',

        monthly: '월간 점검 절차',
        monthlyLede: '모든 비중은 시작 시점의 스냅샷에서 한 번만 측정합니다. 규칙은 아래 순서로 실행되어 하나의 주문 목록을 만듭니다 — 어떤 매매로 다른 종목이 허용범위를 벗어나더라도 그것은 다음 달에 처리하며, 같은 달에 두 번 돌지 않습니다.',
        step: {
            membership: '섹터 편입·편출',
            membershipV: '섹터를 추가하거나 제외합니다. 최대 {max}개입니다. 대체 없이 제외하면 해당 자금을 남은 섹터에 균등 배분하고 목표 비중을 다시 설정합니다.',
            perSector: '섹터별 상한',
            perSectorV: '전체 자산의 {pct}%를 넘는 테마는 상한까지 축소합니다.',
            sleeve: '전략 구분 상한',
            sleeveV: '5개 섹터 합계가 {pct}%를 넘으면 비례 축소합니다 — 전략 구분 전체가 과대한 상태는 전략 구분 내부에서 해결할 수 없습니다.',
            profit: '차익 실현',
            profitV: '목표의 {mult}배 이상인 테마는 평가액의 {pct}%를 매도합니다. 그 상태가 유지되는 한 매월 반복합니다.',
            bands: '자산 허용범위',
            bandsV: '실효 목표 대비 ±{band}%를 벗어난 종목은 이탈폭의 절반을 좁히며, 자금은 자산 전략 내에서 조달하고 흡수합니다.',
        },

        cash: '자금의 흐름',
        cashLede: '두 전략 구분을 독립시키는 규칙과, 투기적 전략에서 자본을 영구적으로 빼내는 두 가지 의도된 예외입니다.',
        event: '이벤트',
        clause: '조항',
        destination: '배분처',
        route: {
            bandTrim: '자산 허용범위 축소',
            cull: '6개월 편출',
            sectorRemoved: '섹터 제외(미대체)',
            capBreach: '섹터별 상한 초과',
            topUp: '섹터 추가 매수',
            sleeveBreach: '전략 구분 상한 초과',
            profitTake: '목표 2배 차익 실현',
            assetSleeve: '자산 전략',
            remainingSectors: '남은 섹터',
            otherSectors: '다른 섹터',
            newCapitalOnly: '새 자금으로만',
            wholePortfolio: '포트폴리오 전체',
        },

        cull: '6개월 편출',
        cullLede: '자금을 넣을 때마다 *그날 기준* 상위 20종목을 매수하므로, 정기변경 사이에는 보유 종목이 20개를 넘습니다. 정기변경마다 정확히 20종목으로 줄입니다.',
        whoStays: '잔류 종목',
        whoStaysV: '정기변경일 기준 적격 시가총액 상위 20종목입니다. 보유 기간이나 상위 20위에 머문 점검 횟수는 고려하지 않습니다',
        whoGoes: '편출 종목',
        whoGoesV: '그날 상위 20위 밖의 모든 종목입니다. 보유 기간이 얼마나 길든, 기준선에 얼마나 근접했든 상관없습니다',
        weights: '비중',
        weightsV: '잔류하는 모든 종목은 해당 순위의 목표 비중으로 초기화되며, 순위는 그날 기준으로 읽습니다 — 7위에서 3위로 오른 종목은 3위의 비중을 받습니다',
        correction: '조정 폭',
        correctionV: '절반이 아니라 전부입니다. 누적된 이탈을 해소하는 유일한 시점입니다',
        sectorsExempt: '섹터',
        sectorsExemptV: '제외됩니다. 이탈을 그대로 이월하며 주기적으로 초기화되지 않습니다',
    },

    sections: {
        start: '시작 가치',
        current: '현재 가치',
        pnl: '손익',
        needsInput: '입력 필요',
        editStart: '시작 가치 수정',
        inclDeposits: '입금 {amount} 포함',
        partsNeedInput: '{count}개 항목 입력 필요',
        since: '{date}부터',
        part: '구성',
        coins: '코인',
        nfts: 'NFT',
        privateBook: '비상장 투자',
    },

    stocks: {
        title: '주식 투자',
        discretionary: '재량 투자',
        bucketsAria: '버킷',
        addBucket: '+ 버킷',
        bucketName: '버킷 이름',
        emptyBucket: '이 버킷에는 보유 종목이 없습니다.',
        allocations: '버킷 배분',
        split: '배분',
        editSplit: '{symbol} 버킷 배분',
        addSplit: '+ 버킷 추가',
        bucket: '버킷',
        shares: '주식 수',
        costBasis: '취득 원가 (USD)',
        errors: {
            load: '버킷을 불러올 수 없습니다.',
            refresh: '지금은 새로고침할 수 없습니다.',
            bucket: '버킷을 추가할 수 없습니다.',
            numbers: '주식 수와 취득 원가는 0 이상의 숫자여야 합니다.',
            duplicate: '각 버킷은 한 번만 지정할 수 있습니다.',
            tooMany: '배분한 주식 수가 보유 수량을 초과합니다.',
            save: '배분을 저장할 수 없습니다.',
        },
    },

    benchmarks: {
        title: '벤치마크 대비 수익률',
        index: 'Kanosei 인덱스',
        spx: 'S&P 500',
        ndx: '나스닥',
        stocks: '주식 투자',
        modeAria: '차트',
        value: '가치',
        compare: 'S&P 500 / 나스닥 대비',
        unavailable: '이 기간의 비교 데이터가 아직 없습니다.',
    },

    nft: {
        col: {
            collection: '컬렉션',
            qty: '수량',
            invested: '투자 금액',
            value: '현재 가치',
            pricedAt: '플로어 기준일',
            pnl: '손익',
            actions: '작업',
        },
        items: '{count}개',
        walletCount: '지갑 {count}개',
        unknown: '{count}개 미확인',
        expand: '{name} 항목 보기',
        collapse: '{name} 항목 숨기기',
        needsInput: '입력 필요',
        needsInputAria: '{field}: 입력 필요',
        viewTx: '거래 보기',
        edited: '수동 입력',
        edit: '{name} 편집',
    },
};

export default ko;
